import { useMemo, type ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Alert } from '../../../components/ui/Alert';
import { Card, CardHeader, StatTile } from '../../../components/ui/Card';
import { ErrorState, LoadingState } from '../../../components/ui/States';
import { Nil, Table, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { BAR_RADIUS_VERTICAL, CHART_AXIS, CHART_GRID, CHART_LEGEND, CHART_TOOLTIP, ChartCard, seriesColor } from '../../../components/ui/chart';
import { formatDayMonth } from '../../../utils/dateFormat';
import { useOutreachCampaign, useOutreachSignal } from '../../../hooks/useOutreach';
import type { OutreachDetail, OutreachWeeks } from '../../../api/outreach';
import { CandidateChips } from './OverviewTable';
import { RepliesList } from './RepliesList';
import { StepsTable } from './StepsTable';
import {
  CONNECTED_TOOLTIP,
  CampaignStatus,
  EMPTY_PERIOD_LABEL,
  campaignDisplayName,
  formatDate,
  formatDateTime,
  formatRate,
  lemlistCampaignUrl,
  ratio,
  signalTabLabel,
  syncFailureText,
} from './format';

const TREND_SERIES = [
  { key: 'contacted', label: 'Contacted' },
  { key: 'connected', label: 'Connected' },
  { key: 'replied', label: 'Replied' },
  { key: 'interested', label: 'Marked interested' },
] as const;

/** "25% of enrolled · 3/12" — conversion from the previous funnel stage. */
function conversion(n: number, previous: number, previousLabel: string): string {
  if (previous === 0) return `no ${previousLabel} yet`;
  return `${formatRate(ratio(n, previous))} of ${previousLabel} · ${n}/${previous}`;
}

interface Tile {
  label: string;
  value: number | string;
  sub: string;
  tooltip?: string;
  /** lemlist's own figure for the same stat, shown only when it differs from ours. */
  lemlist?: number | null;
}

function funnelTiles(data: OutreachDetail): Tile[] {
  const f = data.funnel;
  const ls = data.lemlistSays;
  // Contacted counts the period; In lemlist / Enrolled are running totals, so
  // no rate is drawn between them. Replied is always ÷ contacted, as on the overview.
  const outreach: Tile[] = [
    { label: 'Contacted', value: f.contacted, sub: `${f.touches.toLocaleString('en-GB')} touches in the period`, tooltip: 'Distinct leads with at least one lemlist action in the period', lemlist: ls?.nbLeadsReached },
    { label: 'Connected', value: f.connected, sub: conversion(f.connected, f.invited, 'invited'), tooltip: CONNECTED_TOOLTIP, lemlist: ls?.invitationAccepted },
    { label: 'Replied', value: f.replied, sub: conversion(f.replied, f.contacted, 'contacted'), lemlist: ls?.nbLeadsAnswered },
    { label: 'Marked interested', value: f.interested, sub: 'marked interested in lemlist', lemlist: ls?.nbLeadsInterested },
  ];

  if (!data.signal) {
    return [{ label: 'In lemlist', value: data.leadsInCampaign, sub: 'leads in the campaign now' }, ...outreach];
  }

  const c = data.signal.candidates;
  const proposed = c.proposed + c.confirmed + c.enrolled + c.blocked + c.dismissed;
  const confirmed = c.confirmed + c.enrolled + c.blocked;
  const detected = data.signal.detected;
  return [
    {
      label: 'In source now',
      value: detected ?? '—',
      sub: detected === null ? 'no detector for this play' : 'today’s count, before the play’s filters',
      tooltip:
        data.signal.detectorGap ??
        'Companies (or people) the source holds now, in window. The play then drops trade-only, mega-panel, customer and live-campaign rows, so candidates can be fewer.',
    },
    {
      // No ratio to In source now: candidates are every row ever proposed, the source count is today's.
      label: 'Candidates',
      value: proposed,
      sub: 'ever proposed, any status',
      tooltip: 'Every candidate written for this play since it started, any status. Not comparable with In source now, which counts what the source holds today: candidates can be more than it.',
    },
    { label: 'Confirmed', value: confirmed, sub: conversion(confirmed, proposed, 'candidates'), tooltip: 'A human said yes: confirmed, enrolled or blocked at enrolment' },
    { label: 'Enrolled', value: c.enrolled, sub: conversion(c.enrolled, confirmed, 'confirmed') },
    ...outreach,
  ];
}

/** lg columns per tile count, written out so Tailwind sees every class. More than five wraps at four. */
const TILE_COLS: Record<number, string> = {
  1: 'lg:grid-cols-2',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-4',
};

/** The last instant before an exclusive end, so "to 12 Oct 00:00" reads as 11 Oct. */
function lastDayOf(exclusiveTo: string): string {
  return new Date(new Date(exclusiveTo).getTime() - 1).toISOString();
}

/** A step with nothing sent, failed, opened, accepted or replied. */
function stepIsEmpty(step: OutreachDetail['steps'][number]): boolean {
  return step.sent + step.failed + step.skipped + step.opened + step.accepted + step.replies === 0;
}

/** A not-running campaign gets one neutral note under the header; the header then states no status of its own. */
function isNotRunning(campaign: OutreachDetail['campaign']): boolean {
  return !!campaign?.status && campaign.status !== 'running';
}

/** A quiet, neutral note: context, not a status. Indigo is for links, so the kit's info Alert is not used. */
function Note({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-light bg-surface-secondary px-4 py-3 text-body-sm text-text-secondary">
      {children}
    </div>
  );
}

function HeaderStrip({ data }: { data: OutreachDetail }) {
  const { signal, campaign } = data;
  return (
    <Card padding="sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <p className="text-heading-md text-text-primary">
            {signal ? signalTabLabel(signal.campaignPrefix, signal.label) : campaign ? campaignDisplayName(campaign) : 'Campaign'}
          </p>
          {signal && <p className="text-body-sm text-text-secondary max-w-3xl">{signal.rationale}</p>}
          {campaign ? (
            <div className="flex flex-wrap items-center gap-2 text-body-sm text-text-secondary">
              {signal && (
                <span className="font-medium" title={campaign.name}>
                  {campaignDisplayName(campaign)}
                </span>
              )}
              {!isNotRunning(campaign) && <CampaignStatus campaign={campaign} showRunning />}
              {campaign.senders.length > 0 && (
                <span className="text-text-tertiary">
                  Sent by {campaign.senders.map((s) => s.name ?? s.userId).join(', ')}
                </span>
              )}
              <span className="text-text-tertiary">· {data.leadsInCampaign.toLocaleString('en-GB')} leads in lemlist</span>
            </div>
          ) : (
            <p className="text-body-sm text-warning-dark">
              No lemlist campaign matches {signal ? signal.campaignPrefix : 'this id'}, so nothing can be
              enrolled or counted until one exists.
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0 text-body-xs text-text-tertiary">
          {campaign && (
            <a href={lemlistCampaignUrl(campaign.id)} target="_blank" rel="noreferrer" className="text-accent hover:underline text-body-sm">
              Open in lemlist ↗
            </a>
          )}
          {/* `to` is exclusive (the start of the week in progress): the last day counted is the day before, as on Outbound. */}
          <span>
            {formatDate(data.from)} – {formatDate(lastDayOf(data.to))}
          </span>
        </div>
      </div>
      {signal?.candidates && (
        <div className="mt-3 flex items-center gap-2 text-body-xs text-text-tertiary">
          Candidates, all time: <CandidateChips counts={signal.candidates} />
        </div>
      )}
    </Card>
  );
}

/**
 * Our count, plus lemlist's own campaign-report figure for the same window when
 * the two disagree. When they agree the sub-line stays as it is.
 */
function tileSub(tile: Tile): string {
  if (tile.lemlist == null || typeof tile.value !== 'number' || tile.lemlist === tile.value) return tile.sub;
  return `${tile.sub} · lemlist says ${tile.lemlist.toLocaleString('en-GB')}`;
}

function BlockedGroups({ groups }: { groups: OutreachDetail['blocked'] }) {
  return (
    <Card padding="none">
      <div className="p-4 pb-0">
        <CardHeader
          title="Blocked and dismissed candidates"
          description="Grouped by reason. Click a reason to see the candidates."
        />
      </div>
      <div className="p-4 pt-0">
        {groups.length === 0 ? (
          <p className="py-6 text-center text-body-sm text-text-tertiary">No candidate has been blocked or dismissed.</p>
        ) : (
          <div className="divide-y divide-border-light">
            {groups.map((group) => (
              <details key={group.reason} className="py-2 group">
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-body-sm list-none">
                  <span className="text-text-secondary">
                    <span className="mr-1 inline-block text-text-tertiary transition-transform group-open:rotate-90">›</span>
                    {group.reason}
                  </span>
                  <span className="tabular-nums font-medium text-text-primary">{group.count}</span>
                </summary>
                <div className="mt-2 pl-4">
                  <Table>
                    <THead>
                      <tr>
                        <Th>Company</Th>
                        <Th>Person</Th>
                        <Th>Status</Th>
                        <Th>Decided by</Th>
                        <Th align="right">When</Th>
                      </tr>
                    </THead>
                    <tbody>
                      {group.rows.map((row) => (
                        <Tr key={row.candidateId}>
                          <Td>{row.companyName}</Td>
                          <Td className="text-text-secondary">{row.personName ?? <Nil />}</Td>
                          <Td className="text-text-secondary">{row.status}</Td>
                          <Td className="text-text-secondary">{row.decidedBy ?? 'system'}</Td>
                          <Td align="right" className="whitespace-nowrap text-text-tertiary">
                            {row.decidedAt ? formatDateTime(row.decidedAt) : <Nil />}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function DetailBody({ data, weeks, periodLabel }: { data: OutreachDetail; weeks: OutreachWeeks; periodLabel?: string }) {
  const hasActivity = data.funnel.touches > 0 || data.funnel.contacted > 0 || data.funnel.failed > 0;
  const noLeadsYet = !!data.campaign && data.leadsInCampaign === 0 && data.steps.length > 0 && data.steps.every(stepIsEmpty);
  // Every reason nothing goes out (no sender, not running, nothing in the period, no leads yet) shares one notice.
  const noSender = !!data.campaign && data.campaign.senders.length === 0;
  const notes: ReactNode[] = [];
  if (data.campaign && noSender) {
    notes.push(
      <>
        No sender is set: nobody has sent from this campaign, so lemlist has no one to send its steps from.{' '}
        <a href={lemlistCampaignUrl(data.campaign.id)} target="_blank" rel="noreferrer" className="font-medium underline">
          Set a sender in lemlist ↗
        </a>
      </>,
    );
  }
  if (data.campaign && isNotRunning(data.campaign)) {
    notes.push(`This campaign is ${data.campaign.status} in lemlist, so nothing new will be sent until it runs.`);
  }
  if (!hasActivity) notes.push(EMPTY_PERIOD_LABEL);
  if (noLeadsYet) {
    notes.push(
      `${data.steps.length} sequence step${data.steps.length === 1 ? '' : 's'} set up · the campaign has no leads yet, so nothing has been sent.`,
    );
  }
  // With no activity, zero tiles and an empty chart only repeat the one note; tiles that still hold a figure stay.
  const tiles = funnelTiles(data).filter((tile) => hasActivity || (tile.value !== 0 && tile.value !== '—'));

  const trend = useMemo(
    () =>
      data.weekly.map((w) => {
        return { ...w, label: formatDayMonth(`${w.week.slice(0, 10)}T12:00:00Z`) };
      }),
    [data.weekly],
  );
  const trendEmpty = data.weekly.every((w) => w.contacted + w.connected + w.replied + w.interested === 0);

  return (
    <div className="space-y-5">
      <HeaderStrip data={data} />

      {data.signal?.detectorGap && <Alert variant="warning" title="No detector">{data.signal.detectorGap}</Alert>}

      {/*
        One notice for every reason nothing goes out (no sender, not running, no activity, no leads),
        not four stacked boxes. Amber only when there is something to fix (no sender); otherwise a quiet note.
      */}
      {notes.length > 0 &&
        (() => {
          const body =
            notes.length === 1 ? (
              notes[0]
            ) : (
              <ul className="list-disc space-y-1 pl-4">
                {notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            );
          return noSender ? <Alert variant="warning" title="Nothing is going out from this campaign">{body}</Alert> : <Note>{body}</Note>;
        })()}

      {tiles.length > 0 && (
      <div className={`grid grid-cols-2 gap-3 ${TILE_COLS[Math.min(tiles.length, 6)]}`}>
        {tiles.map((tile) => (
          <StatTile
            key={tile.label}
            label={tile.label}
            value={typeof tile.value === 'number' ? tile.value.toLocaleString('en-GB') : tile.value}
            sub={tileSub(tile)}
            tooltip={
              tile.lemlist != null && tile.lemlist !== tile.value
                ? `${tile.tooltip ? `${tile.tooltip}. ` : ''}lemlist's own report counts this differently or the sync is behind.`
                : tile.tooltip
            }
          />
        ))}
      </div>
      )}

      {data.funnel.failed > 0 && (
        <Alert variant="warning">
          {data.funnel.failed} action{data.funnel.failed === 1 ? '' : 's'} failed in this period. Hover the Failed column in the
          step table for the reasons.
          {data.lemlistSays?.messagesNotSent != null && data.lemlistSays.messagesNotSent !== data.funnel.failed &&
            ` lemlist reports ${data.lemlistSays.messagesNotSent} not sent.`}
        </Alert>
      )}

      {data.funnel.messagesBeforeAccept > 0 && (
        <Alert variant="warning" title="Sequence design">
          {data.funnel.messagesBeforeAccept} LinkedIn message{data.funnel.messagesBeforeAccept === 1 ? '' : 's'} fired before the
          invite was accepted (lemlist: “pending invitation”). Not counted as failures: the message step comes too soon after the invite.
        </Alert>
      )}

      {data.campaign && data.lemlistSaysError && (
        <Alert variant="error">Could not load lemlist's own stats to cross-check these counts: {data.lemlistSaysError}</Alert>
      )}

      {hasActivity && (
      <ChartCard
        title="Weekly funnel"
        description={
          periodLabel
            ? `${periodLabel}, Brussels weeks.`
            : weeks === 'all'
              ? 'Every full week since the first activity, Brussels weeks.'
              : `Last ${weeks} full weeks, Brussels weeks.`
        }
        chart={
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trend}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="label" {...CHART_AXIS} />
              <YAxis {...CHART_AXIS} allowDecimals={false} />
              <Tooltip {...CHART_TOOLTIP} />
              <Legend {...CHART_LEGEND} formatter={(value) => <span className="text-text-secondary">{value}</span>} />
              {TREND_SERIES.map((series, index) => (
                <Bar key={series.key} dataKey={series.key} name={series.label} fill={seriesColor(index)} radius={[...BAR_RADIUS_VERTICAL]} isAnimationActive={false} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        }
        isEmpty={trendEmpty}
        emptyLabel={EMPTY_PERIOD_LABEL}
      />
      )}

      {data.campaign && !noLeadsYet && <StepsTable steps={data.steps} />}

      {/* No replies in the period: the Replied tile already says 0, so no empty section. */}
      {data.campaign && data.replies.length > 0 && <RepliesList replies={data.replies} />}

      {data.signal && <BlockedGroups groups={data.blocked} />}

      <p className={data.lemlistSyncFailure ? 'text-body-xs text-warning-dark font-medium' : 'text-body-xs text-text-tertiary'}>
        {data.lemlistLastSyncAt ? `Last lemlist sync ${formatDateTime(data.lemlistLastSyncAt)}.` : 'lemlist has never synced.'}
        {data.lemlistSyncFailure && ` ${syncFailureText(data.lemlistSyncFailure)}`}
      </p>
    </div>
  );
}

function DetailState({
  query,
  weeks,
  what,
  periodLabel,
}: {
  query: { data: OutreachDetail | undefined; isLoading: boolean; error: Error | null; refetch: () => unknown };
  weeks: OutreachWeeks;
  what: string;
  periodLabel?: string;
}) {
  if (query.isLoading) return <LoadingState rows={5} />;
  if (query.error) {
    return <ErrorState title={`Couldn’t load ${what}`} error={query.error} onRetry={() => query.refetch()} />;
  }
  if (!query.data) return null;
  return <DetailBody data={query.data} weeks={weeks} periodLabel={periodLabel} />;
}

interface DetailProps {
  weeks: OutreachWeeks;
  /** What the parent's period chips cover, e.g. "4 full weeks, since lemlist data starts (7 Sep)". */
  periodLabel?: string;
}

export function SignalDetail({ playId, weeks, periodLabel }: DetailProps & { playId: string }) {
  const query = useOutreachSignal(playId, weeks);
  return <DetailState query={query} weeks={weeks} what="the signal" periodLabel={periodLabel} />;
}

export function CampaignDetail({ campaignId, weeks, periodLabel }: DetailProps & { campaignId: string }) {
  const query = useOutreachCampaign(campaignId, weeks);
  return <DetailState query={query} weeks={weeks} what="the campaign" periodLabel={periodLabel} />;
}

/** One signal's or one campaign's detail page — same layout, signal stages only for a signal. */
export function DetailView({ kind, id, weeks, periodLabel }: DetailProps & { kind: 'signal' | 'campaign'; id: string }) {
  return kind === 'signal' ? (
    <SignalDetail playId={id} weeks={weeks} periodLabel={periodLabel} />
  ) : (
    <CampaignDetail campaignId={id} weeks={weeks} periodLabel={periodLabel} />
  );
}
